import FormField from 'components/form-field/form-field';
import FieldSuggestions, {
  Suggestion,
} from 'components/field-suggestions/field-suggestions';
import { useFormikContext } from 'formik';
import React, { FunctionComponent, useMemo } from 'react';
import { Button, Form, Modal } from 'semantic-ui-react';
import { PhoneNumber } from 'utils';
import {
  DocumentSendFormAdditionalFields,
  DocumentSendFormModalFields,
} from './document-send-form-modal-container';

type Signatory = {
  givenName: string;
  familyName: string;
  telephone: string;
  isLegalRepresentative?: boolean;
};

type ProMandateSendModalProps = {
  open: boolean;
  onClose: () => void;
  additionalFields?: DocumentSendFormAdditionalFields;
  receiptEmailSuggestions?: string[];
  proPersonSuggestions?: Signatory[] | undefined;
};

const DocumentSendFormModalView: FunctionComponent<
  ProMandateSendModalProps
> = ({
  open,
  receiptEmailSuggestions,
  proPersonSuggestions,
  additionalFields,
  onClose,
}) => {
  const { handleSubmit, isSubmitting, handleReset, submitForm } =
    useFormikContext<DocumentSendFormModalFields>();

  const signatorySuggestions = useMemo(() => {
    return (
      proPersonSuggestions?.map((signatory) => ({
        key: signatory.telephone,
        label: (
          <>
            {signatory.givenName} {signatory.familyName} -{' '}
            {PhoneNumber.format(signatory.telephone || 'error')}
            {signatory.isLegalRepresentative ? ' (Représentant légal)' : ''}
          </>
        ),
        fields: {
          telephone: signatory.telephone,
          ...(additionalFields?.givenName
            ? { givenName: signatory.givenName }
            : {}),
          ...(additionalFields?.familyName
            ? { familyName: signatory.familyName }
            : {}),
        },
      })) || []
    );
  }, [proPersonSuggestions, additionalFields]);

  const receiptSuggestions: Suggestion[] | undefined = useMemo(
    () =>
      receiptEmailSuggestions?.map((email) => ({
        key: email,
        label: email,
        fields: { proReceiptEmail: email },
      })),
    [receiptEmailSuggestions],
  );

  return (
    <Modal
      open={open}
      closeOnDimmerClick={false}
      closeOnEscape={false}
      closeIcon={true}
      onClose={onClose}
    >
      <Modal.Header>Envoyer le document</Modal.Header>
      <Modal.Content>
        <Form onSubmit={handleSubmit} onReset={handleReset}>
          <FieldSuggestions
            label="Envoyer le lien de signature à :"
            suggestions={signatorySuggestions}
            placeholder="Sélectionnez le numéro..."
            required
          >
            <FormField
              type="telephone"
              label="Numéro"
              name="telephone"
              placeholder="Ex: 06 44 64 27 74"
              required
            />
            {additionalFields?.familyName && (
              <FormField
                type="text"
                placeholder="Ex: Nom"
                label="Nom"
                name="familyName"
                required
              />
            )}
            {additionalFields?.givenName && (
              <FormField
                type="text"
                placeholder="Ex: Prénom"
                label="Prénom"
                name="givenName"
                required
              />
            )}
          </FieldSuggestions>
          <FieldSuggestions
            label="Email d'accusé de réception du pro :"
            suggestions={receiptSuggestions}
            placeholder="Sélectionnez l'email de réception du mandat..."
            required
          >
            <FormField
              type="email"
              label="Email"
              name="proReceiptEmail"
              placeholder="Ex : machin@truc.fr"
              required
            />
          </FieldSuggestions>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button type="button" onClick={onClose}>
          Fermer
        </Button>
        <Button
          type="submit"
          loading={isSubmitting}
          disabled={isSubmitting}
          primary
          onClick={submitForm}
        >
          Envoyer
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default DocumentSendFormModalView;
